/**
 * Impresión del comprobante.
 *
 * Hay dos caminos y el orden importa. Primero se intenta el puente, un servicio
 * chico que corre en el equipo del mostrador y le habla a la impresora térmica
 * en su propio idioma: sale sin diálogo y con corte de papel. Si no contesta,
 * se cae a la impresión del navegador, que abre el diálogo de siempre.
 *
 * La vuelta atrás no es un detalle: un cajero no puede quedarse sin poder
 * entregar el comprobante porque un servicio no arrancó. Entre un ticket feo y
 * ningún ticket, gana el feo.
 */

import type { Comprobante } from './tipos'

/**
 * El puente escucha en la máquina del cajero, no en el servidor. Siempre es
 * localhost: el navegador que corre el POS es el que está al lado de la
 * impresora.
 *
 * El puerto se puede cambiar desde la consola del navegador si 9123 ya está
 * ocupado en ese equipo:
 *
 *   localStorage.setItem('puente-impresion', 'http://127.0.0.1:9200')
 */
const PUENTE_POR_DEFECTO = 'http://127.0.0.1:9123'

function direccionPuente(): string {
    try {
        return localStorage.getItem('puente-impresion') ?? PUENTE_POR_DEFECTO
    } catch {
        // Navegador con el almacenamiento bloqueado.
        return PUENTE_POR_DEFECTO
    }
}

/**
 * Cuánto se espera al puente antes de resignarse.
 *
 * Corto a propósito. Si el servicio no está, lo que se quiere es que aparezca
 * el diálogo del navegador cuanto antes, no que el cajero mire una pantalla
 * quieta. Una impresora que sí está contesta en decenas de milisegundos.
 */
const ESPERA_MS = 1500

export type ResultadoImpresion =
    | { via: 'puente' }
    | { via: 'navegador'; motivo: string }

/**
 * Manda el comprobante al puente. Devuelve por dónde salió, para poder avisarlo
 * en pantalla cuando no fue por donde debía.
 */
export async function imprimirComprobante(comprobante: Comprobante): Promise<ResultadoImpresion> {
    const corte = new AbortController()
    const reloj = setTimeout(() => corte.abort(), ESPERA_MS)

    try {
        const respuesta = await fetch(`${direccionPuente()}/imprimir`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(comprobante),
            signal: corte.signal,
        })

        if (respuesta.ok) return { via: 'puente' }

        // El puente contestó pero no imprimió: sin papel, impresora apagada,
        // nombre de cola equivocado. El mensaje es suyo y vale la pena mostrarlo
        // tal cual, porque dice qué revisar.
        const cuerpo = (await respuesta.json().catch(() => null)) as { error?: string } | null

        imprimirConNavegador()
        return { via: 'navegador', motivo: cuerpo?.error ?? `El puente respondió ${respuesta.status}.` }
    } catch {
        // No está corriendo, o el navegador bloqueó la llamada a la red local.
        imprimirConNavegador()
        return { via: 'navegador', motivo: 'El puente de impresión no respondió.' }
    } finally {
        clearTimeout(reloj)
    }
}

/**
 * La impresión de siempre: el navegador arma la página con la hoja de estilos
 * de impresión y abre su diálogo.
 */
export function imprimirConNavegador(): void {
    window.print()
}

/** Para la pantalla de configuración: comprobar el puente sin registrar una venta. */
export async function probarPuente(): Promise<{ ok: boolean; detalle: string }> {
    const corte = new AbortController()
    const reloj = setTimeout(() => corte.abort(), ESPERA_MS * 4)

    try {
        const respuesta = await fetch(`${direccionPuente()}/prueba`, {
            method: 'POST',
            signal: corte.signal,
        })

        if (respuesta.ok) {
            return { ok: true, detalle: 'Se envió la página de prueba a la impresora.' }
        }

        const cuerpo = (await respuesta.json().catch(() => null)) as { error?: string } | null
        return { ok: false, detalle: cuerpo?.error ?? `El puente respondió ${respuesta.status}.` }
    } catch {
        return {
            ok: false,
            detalle: `No se pudo contactar al puente en ${direccionPuente()}. ` +
                'Revise que el servicio esté corriendo en este equipo.',
        }
    } finally {
        clearTimeout(reloj)
    }
}
